import { useGleapVisibility } from '@aibees/elephant/hooks/use-gleap-visibility';
import { SEARCH_PARAMS_STORE_KEY } from '@aibees/elephant/store/search-params-store/search-params-store';
import { AuthProvider } from '@aibees/shared/auth';
import {
  ErrorBoundary,
  MotionLazy,
  NProgress,
  SettingsDrawer,
  SettingsProvider,
  SnackbarProvider,
} from '@aibees/shared/components';
import {
  AMPLITUDE_API_KEY,
  AMPLITUDE_CONFIG,
  CUSTOMERIO_CONFIG,
  CUSTOMERIO_SITE_ID,
  GLEAP_CONFIG,
  GLEAP_TOKEN,
  MIXPANEL_CONFIG,
  MIXPANEL_TOKEN,
  POSTHOG_API_KEY,
  POSTHOG_CONFIG,
} from '@aibees/shared/constants';
import {
  CustomerIOProvider,
  GleapProvider,
  MixPanelProvider,
  PostHogProvider,
} from '@aibees/shared/context';
import { AmplitudeProvider } from '@aibees/shared/context/amplitude';
import { QueryProvider } from '@aibees/shared/http';
import { ReactQueryProvider } from '@aibees/shared/http/base-query-client';
import ThemeProvider from '@aibees/shared/theme';
import { PropsWithChildren } from 'react';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'simplebar-react/dist/simplebar.min.css';

export default function RootLayout({ children }: PropsWithChildren) {
  useGleapVisibility();

  return (
    <MixPanelProvider
      token={MIXPANEL_TOKEN}
      config={MIXPANEL_CONFIG}
      projectName="AI-Elephant"
    >
      <CustomerIOProvider
        siteId={CUSTOMERIO_SITE_ID}
        config={CUSTOMERIO_CONFIG}
      >
        <GleapProvider token={GLEAP_TOKEN} config={GLEAP_CONFIG}>
          <PostHogProvider apiKey={POSTHOG_API_KEY} config={POSTHOG_CONFIG}>
            <AmplitudeProvider
              token={AMPLITUDE_API_KEY}
              config={AMPLITUDE_CONFIG}
            >
              <SettingsProvider
                defaultSettings={{
                  themeMode: 'light', // 'light' | 'dark'
                  themeDirection: 'ltr', //  'rtl' | 'ltr'
                  themeContrast: 'bold', // 'default' | 'bold'
                  themeLayout: 'vertical', // 'vertical' | 'mini'
                  themeColorPresets: 'cyan', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
                  themeStretch: true,
                  enableSettingButton: true,
                }}
              >
                <ThemeProvider>
                  <SnackbarProvider>
                    <MotionLazy>
                      <ErrorBoundary>
                        <SettingsDrawer />
                        <NProgress />
                        <QueryProvider>
                          <ReactQueryProvider>
                            <AuthProvider
                              localStorageKeysToClearOnLogout={[
                                SEARCH_PARAMS_STORE_KEY,
                              ]}
                            >
                              {children}
                            </AuthProvider>
                          </ReactQueryProvider>
                        </QueryProvider>
                      </ErrorBoundary>
                    </MotionLazy>
                  </SnackbarProvider>
                </ThemeProvider>
              </SettingsProvider>
            </AmplitudeProvider>
          </PostHogProvider>
        </GleapProvider>
      </CustomerIOProvider>
    </MixPanelProvider>
  );
}
